import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { 
  FileText, 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  ExternalLink,
  Settings,
  Link2,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FORM_CATEGORIES = [
  { id: 'intake', name: 'Intake Form' },
  { id: 'consent', name: 'Consent Form' },
  { id: 'assessment', name: 'Assessment' },
  { id: 'enrollment', name: 'Enrollment Form' },
  { id: 'exit', name: 'Exit Form' },
  { id: 'general', name: 'General Document' }
];

const JotformsPage = () => {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [prefillFields, setPrefillFields] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    jotform_form_id: '',
    form_category: 'general',
    program_types: [],
    states: [],
    is_required: false,
    auto_save_to_documents: true,
    auto_create_case_note: true,
    is_active: true,
    field_mappings: {}
  });

  useEffect(() => {
    fetchForms();
    fetchProgramTypes();
    fetchPrefillFields();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/admin/jotforms`);
      setForms(response.data.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramTypes = async () => {
    try {
      const response = await axios.get(`${API}/enrollment-types`);
      setProgramTypes(response.data.enrollment_types || []);
    } catch (error) {
      console.error('Failed to fetch program types:', error);
    }
  };

  const fetchPrefillFields = async () => {
    try {
      const response = await axios.get(`${API}/jotforms/prefill-fields`);
      setPrefillFields(response.data.fields || []);
    } catch (error) {
      console.error('Failed to fetch prefill fields:', error);
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      description: '',
      jotform_form_id: '',
      form_category: 'general',
      program_types: [],
      states: [],
      is_required: false,
      auto_save_to_documents: true,
      auto_create_case_note: true,
      is_active: true,
      field_mappings: {}
    });
    setEditingForm(null);
  };

  const handleOpenDialog = (form = null) => {
    if (form) {
      setEditingForm(form);
      setFormData({
        name: form.name || '',
        description: form.description || '',
        jotform_form_id: form.jotform_form_id || '',
        form_category: form.form_category || 'general',
        program_types: form.program_types || [],
        states: form.states || [],
        is_required: form.is_required || false,
        auto_save_to_documents: form.auto_save_to_documents !== false,
        auto_create_case_note: form.auto_create_case_note !== false,
        is_active: form.is_active !== false,
        field_mappings: form.field_mappings || {}
      });
    } else {
      resetFormData();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.jotform_form_id) {
      toast.error('Form name and Jotform ID are required');
      return;
    }

    setSaving(true);
    try {
      if (editingForm) {
        await axios.put(`${API}/admin/jotforms/${editingForm.id}`, formData);
        toast.success('Form updated successfully');
      } else {
        await axios.post(`${API}/admin/jotforms`, formData);
        toast.success('Form added successfully');
      }
      setShowDialog(false);
      resetFormData();
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Are you sure you want to delete "${form.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/jotforms/${form.id}`);
      toast.success('Form deleted successfully');
      fetchForms();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const toggleActive = async (form) => {
    try {
      await axios.put(`${API}/admin/jotforms/${form.id}`, {
        ...form,
        is_active: !form.is_active
      });
      toast.success(`Form ${form.is_active ? 'deactivated' : 'activated'}`);
      fetchForms();
    } catch (error) {
      toast.error('Failed to update form status');
    }
  };

  const addFieldMapping = (jotformField, profileField) => {
    setFormData(prev => ({
      ...prev,
      field_mappings: {
        ...prev.field_mappings,
        [jotformField]: profileField
      }
    }));
  };

  const removeFieldMapping = (jotformField) => {
    const newMappings = { ...formData.field_mappings };
    delete newMappings[jotformField];
    setFormData(prev => ({
      ...prev,
      field_mappings: newMappings
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jotforms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-8 h-8 text-primary" />
            Jotform Integration
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure forms from Jotform for use with participants
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-form-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Form
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-200">How Jotform Integration Works</p>
              <ul className="text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                <li>1. Add your Jotform Form ID below (found in your Jotform URL)</li>
                <li>2. Configure which participant fields should pre-fill the form</li>
                <li>3. When a user launches the form, it will be pre-filled with participant data</li>
                <li>4. Upon submission, the PDF is automatically saved to the case and a note is created</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Forms List */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle>Configured Forms</CardTitle>
          <CardDescription>
            {forms.length} form{forms.length !== 1 ? 's' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {forms.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No forms configured</h3>
              <p className="text-muted-foreground">Add your first Jotform to get started</p>
              <Button onClick={() => handleOpenDialog()} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add Form
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Form Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Jotform ID</TableHead>
                  <TableHead>Programs</TableHead>
                  <TableHead>Mappings</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {forms.map((form) => (
                  <TableRow key={form.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{form.name}</p>
                        {form.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {form.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {FORM_CATEGORIES.find(c => c.id === form.form_category)?.name || form.form_category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {form.jotform_form_id}
                        </code>
                        <a 
                          href={`https://form.jotform.com/${form.jotform_form_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>
                      {form.program_types?.length > 0 ? (
                        <Badge variant="secondary">{form.program_types.length} programs</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">All programs</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {Object.keys(form.field_mappings || {}).length > 0 ? (
                        <Badge variant="secondary">
                          {Object.keys(form.field_mappings).length} fields
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">Default only</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={form.is_active ? 'default' : 'secondary'}
                        className="cursor-pointer"
                        onClick={() => toggleActive(form)}
                      >
                        {form.is_active ? (
                          <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
                        ) : (
                          'Inactive'
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(form)}
                          data-testid={`edit-form-${form.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(form)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-form-${form.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Form Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingForm ? 'Edit Form' : 'Add New Jotform'}</DialogTitle>
            <DialogDescription>
              {editingForm 
                ? 'Update the form configuration'
                : 'Configure a Jotform for use with participant profiles'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Form Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Intake Consent Form"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="form-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jotform_id">Jotform Form ID *</Label>
                <Input
                  id="jotform_id"
                  placeholder="e.g., 240123456789012"
                  value={formData.jotform_form_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, jotform_form_id: e.target.value }))}
                  data-testid="jotform-id-input"
                />
                <p className="text-xs text-muted-foreground">
                  Found in your Jotform URL: form.jotform.com/[FORM_ID]
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of what this form is for..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.form_category}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, form_category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FORM_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Restrict to Programs</Label>
                <Select
                  value={formData.program_types?.length > 0 ? formData.program_types[0] : '_all'}
                  onValueChange={(v) => setFormData(prev => ({ 
                    ...prev, 
                    program_types: v === '_all' ? [] : [v]
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Programs</SelectItem>
                    {programTypes.map(pt => (
                      <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Settings */}
            <div className="space-y-3 pt-2">
              <Label className="text-base">Settings</Label>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">Auto-save to Documents</p>
                  <p className="text-xs text-muted-foreground">Automatically save completed form PDF to case documents</p>
                </div>
                <Switch
                  checked={formData.auto_save_to_documents}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, auto_save_to_documents: v }))}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">Auto-create Case Note</p>
                  <p className="text-xs text-muted-foreground">Automatically create a case note when form is submitted</p>
                </div>
                <Switch
                  checked={formData.auto_create_case_note}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, auto_create_case_note: v }))}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">Required for Enrollment</p>
                  <p className="text-xs text-muted-foreground">Mark this form as required for compliance</p>
                </div>
                <Switch
                  checked={formData.is_required}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_required: v }))}
                />
              </div>
            </div>

            {/* Field Mappings */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Field Mappings</Label>
                  <p className="text-xs text-muted-foreground">Map Jotform field names to participant profile fields for pre-fill</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addFieldMapping('', '')}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Mapping
                </Button>
              </div>
              
              <div className="space-y-2">
                {Object.entries(formData.field_mappings).map(([jotformField, profileField], index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="Jotform field name"
                      value={jotformField}
                      onChange={(e) => {
                        const newMappings = { ...formData.field_mappings };
                        delete newMappings[jotformField];
                        newMappings[e.target.value] = profileField;
                        setFormData(prev => ({ ...prev, field_mappings: newMappings }));
                      }}
                      className="flex-1"
                    />
                    <Link2 className="w-4 h-4 text-muted-foreground" />
                    <Select
                      value={profileField}
                      onValueChange={(v) => {
                        const newMappings = { ...formData.field_mappings };
                        newMappings[jotformField] = v;
                        setFormData(prev => ({ ...prev, field_mappings: newMappings }));
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select profile field" />
                      </SelectTrigger>
                      <SelectContent>
                        {prefillFields.map(field => (
                          <SelectItem key={field.id} value={field.id}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFieldMapping(jotformField)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                {Object.keys(formData.field_mappings).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No custom field mappings. Default fields (name, email, phone, etc.) will be pre-filled automatically.
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingForm ? 'Update Form' : 'Add Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JotformsPage;
